import { Component, EventEmitter, Inject, Input, OnInit, Output } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { UntilDestroy } from '@ngneat/until-destroy';
import { ToastrService } from 'ngx-toastr';
import { Subject, firstValueFrom } from 'rxjs';
import { DefaultUserData, UserAccountType, UserData } from 'src/app/data-types/user-data';
import { ActionService } from 'src/app/services/action.service';
import { AddressService } from 'src/app/services/address.service';
import { ToolsService } from 'src/app/services/tools.service';
import { UserService } from 'src/app/services/user.service';
import { ILead } from '../lead.interface';
import { LeadService } from '../lead.service';
import { AlertService } from 'src/app/services/alert.service';

export const LeadFormDlgDataParams = {
    width: '604px',
    position: { top: '80px' },
};

export interface ILeadFormDlgData {
    leadToEdit?: ILead;
}

export interface ILeadFormDlgResult {
    status?: boolean;
    lead: UserData;
}

@UntilDestroy()
@Component({
    selector: 'app-lead-form',
    templateUrl: './lead-form.component.html',
    styleUrls: ['./lead-form.component.scss'],
})
export class LeadFormComponent implements OnInit {
    @Input() openLeadForm$: Subject<void>;
    @Input() withAction = false;
    @Output() leadSaved = new EventEmitter();

    Validators = Validators;

    leadForm: FormGroup;
    leadToEdit = null;
    actionTypes = [];

    customerTypes = [
        { id: UserAccountType.Customer, name: 'Private' },
        { id: UserAccountType.Trade, name: 'Trade' },
    ];

    get formTitle(): string {
        return this.leadToEdit ? 'Edit Lead' : 'New Lead';
    }

    constructor(
        @Inject(MAT_DIALOG_DATA) public data: ILeadFormDlgData,
        private fb: FormBuilder,
        private userService: UserService,
        private leadSerivce: LeadService,
        private _toolsService: ToolsService,
        private _addressService: AddressService,
        private toastrService: ToastrService,
        private dialogRef: MatDialogRef<LeadFormComponent>,
        private actionService: ActionService,
        private _alertService: AlertService
    ) {
        this.leadForm = this.fb.group({
            firstname: ['', [Validators.required]],
            surname: ['', [Validators.required]],
            phone: ['', [Validators.required]],
            email: ['', [Validators.required, Validators.email]],
            customerType: [UserAccountType.Customer, [Validators.required]],
            country: [''],
            company: [''],
            position: [''],
            address: [''],
            city: [''],
            postcode: [''],
            leadType: [0, [Validators.required]],
            isAction: [false],
            action: this.fb.group({
                actionType: ['', []],
                text: ['', []],
            }),
        });

        if (this.data?.leadToEdit) {
            this.leadToEdit = this.data.leadToEdit;
        }
    }

    ngOnInit(): void {
        if (this.leadToEdit) {
            this.setFormData();
        }
        if (this.withAction) {
            this.actionService.getActionTypes().subscribe((actionTypes) => {
                this.actionTypes = actionTypes;
            });
        }
        this.leadForm.valueChanges.pipe().subscribe(() => {
            // actionType && text required
            const actionFG: FormGroup = this.leadForm.controls.action as FormGroup;
            if (this.leadForm.controls.isAction.value) {
                actionFG.controls.actionType.setValidators([Validators.required]);
                actionFG.controls.text.setValidators([Validators.required]);
            } else {
                actionFG.controls.actionType.clearValidators();
                actionFG.controls.actionType.setErrors(null);
                actionFG.controls.text.clearValidators();
                actionFG.controls.text.setErrors(null);
            }

            // company required
            if (this.leadForm.controls.customerType.value === UserAccountType.Trade) {
                this.leadForm.controls.company.setValidators([Validators.required]);
            } else {
                this.leadForm.controls.company.clearValidators();
                this.leadForm.controls.company.setErrors(null);
            }
        });
    }

    async saveLead() {
        const lead = { ...DefaultUserData, prospect: 1 };

        const formData = this.leadForm.getRawValue();

        lead.accountType = formData.customerType;
        lead.username = formData.email;

        lead.contact.firstname = formData.firstname;
        lead.contact.surname = formData.surname;
        lead.contact.email = formData.email;
        lead.contact.mobile = formData.phone.e164Number;
        lead.contact.mobDialCode = formData.phone.dialCode;
        lead.contact.companyName = formData.company ? formData.company : null;
        lead.contact.fullAddress = formData.address ? formData.address : null;
        lead.contact.town = formData.city;
        lead.contact.country = formData.country;
        lead.contact.postcode = formData.postcode;
        lead.contact.companyName = formData.company;
        lead.contact.jobTitle = formData.position;

        lead.contact.isWarm = formData.leadType;

        let addressId = null;
        if (!this.leadToEdit || !this.leadToEdit.addressId) {
            addressId = this._toolsService.newUUID();
        }

        lead.contact.addressId = addressId ? addressId : this.leadToEdit.addressId;
        const address = {
            id: addressId ? addressId : this.leadToEdit.addressId,
            add1: formData.address ? formData.address : null,
            add2: '',
            add3: '',
            county: '',
            town: formData.city,
            postcode: formData.postcode,
            country: formData.country,
            userId: lead.id,
        };
        let leadResult;
        if (this.leadToEdit) {
            lead.id = this.leadToEdit.id;
            lead.contact.id = this.leadToEdit.contactId;
            leadResult = await firstValueFrom(this.userService.update(lead)).catch((error) => {
                this._alertService.error([error.error.message]);
            });
            leadResult = leadResult.data;
        } else {
            try {
                leadResult = await this.userService.createUserNew(lead).catch((error) => {
                    this._alertService.error([error.error.message]);
                });
                if (this.needToCreateAction()) {
                    await firstValueFrom(
                        this.actionService.createAction({
                            ...this.leadForm.get('action').value,
                            title: 'Create lead form submission',
                        })
                    );
                }
            } catch (err) {
                this.toastrService.error('', err.error.message, { timeOut: 0 });
                return;
            }
        }

        if (addressId) {
            await firstValueFrom(this._addressService.create(address));
        } else {
            await firstValueFrom(this._addressService.update(address));
        }

        this.leadSaved.emit(leadResult);
        this.dialogRef.close({ status: true, lead: leadResult });
    }

    fieldIsValid(filedName: string): boolean {
        return (
            this.leadForm.get(filedName).invalid &&
            (this.leadForm.get(filedName).dirty || this.leadForm.get(filedName).touched)
        );
    }

    close() {
        this.dialogRef.close({ status: false });
    }

    private setFormData() {
        this.leadForm.patchValue({
            customerType: this.leadSerivce.customerToUserAccountTypeMap[this.leadToEdit.customerType],
            firstname: this.leadToEdit.firstname,
            surname: this.leadToEdit.surname,
            email: this.leadToEdit.email,
            phone: this.leadToEdit.phone,
            company: this.leadToEdit.companyName,
            position: this.leadToEdit.jobTitle,
            address: this.joinAddress(this.leadToEdit),
            city: this.leadToEdit.town,
            country: this.leadToEdit.country,
            postcode: this.leadToEdit.postcode,
            leadType: this.leadToEdit.isWarm,
        });
    }

    private joinAddress(lead) {
        let joinedAddress = '';
        joinedAddress += lead.add1 ? lead.add1 : '';
        joinedAddress += lead.add2 ? lead.add2 : '';
        joinedAddress += lead.add3 ? lead.add3 : '';
        return joinedAddress;
    }

    private needToCreateAction(): boolean {
        return this.withAction && this.leadForm.controls.isAction.value;
    }
}
